import type {BunkSelection} from '../BunkOptions';
import type {PhotoPreviewDraft} from './RoomPhotoPreview';
import {roomViews,withRoomViews,type PhotoPurchaseRoom} from './photo-purchase-rooms';

export type BunkSource={roomId:string;viewId:string;index:number};
export type BunkDestination={roomId?:string;roomName?:string;viewId?:string;photoPreview?:PhotoPreviewDraft};

/** Migrate duplicate room-local labels once, retaining already unique order labels. */
export function numberPurchaseBunks(rooms:PhotoPurchaseRoom[]):PhotoPurchaseRoom[]{
 const used=new Set<number>();
 let next=Math.max(0,...rooms.flatMap(r=>roomViews(r).flatMap(v=>v.bunkNumbers??[])))+1;
 return rooms.map(room=>{
  let changed=false;
  const views=roomViews(room).map(view=>({...view,bunkNumbers:view.bunks.map((_,i)=>{
   let n=view.bunkNumbers?.[i]??next++;
   if(used.has(n)){n=next++;changed=true;}used.add(n);return n;
  })}));
  return changed?withRoomViews(room,views,room.activeViewId??views[0].id):room;
 });
}

/** Build an immutable transaction. Nothing enters the saved order before confirmation. */
export function placePurchaseBunk(input:PhotoPurchaseRoom[],bunk:BunkSelection,destination:BunkDestination,source?:BunkSource){
 const rooms=numberPurchaseBunks(input);
 const sourceView=source&&roomViews(rooms.find(r=>r.id===source.roomId)!).find(v=>v.id===source.viewId);
 const number=sourceView?.bunkNumbers?.[source!.index]??Math.max(0,...rooms.flatMap(r=>roomViews(r).flatMap(v=>v.bunkNumbers??[])))+1;
 const roomId=destination.roomId??crypto.randomUUID();
 let target=rooms.find(r=>r.id===roomId);
 if(destination.roomId&&!target)throw Error('Please choose a room again.');
 if(!target&&rooms.length>=20)throw Error('Your order already has 20 rooms.');
 let roomNumber=rooms.length+1;while(rooms.some(r=>r.name===`Bunk room ${roomNumber}`))roomNumber++;
 if(!target)target={id:roomId,name:destination.roomName?.trim()||`Bunk room ${roomNumber}`,bunks:[]};
 const existing=destination.viewId?roomViews(target).find(v=>v.id===destination.viewId):undefined;
 if(destination.viewId&&!existing)throw Error('Please choose a photo again.');
 const sameView=source?.roomId===roomId&&source?.viewId===destination.viewId;
 if(existing&&existing.bunks.length-(sameView?1:0)>=4)throw Error('Use another photo for this bunk. This photo already has four bunks.');
 let updated=rooms.map(room=>{
  if(!source||room.id!==source.roomId)return room;
  const views=roomViews(room).flatMap(view=>{
   if(view.id!==source.viewId)return [view];
   const bunks=view.bunks.filter((_,i)=>i!==source.index);
   if(!bunks.length)return [];
   const photo=view.photoPreview,beds=photo?.beds.filter((_,i)=>i!==source.index),active=beds?.find(b=>b.id===photo?.active)??beds?.[0];
   return [{...view,bunks,bunkNumbers:view.bunkNumbers?.filter((_,i)=>i!==source.index),photoPreview:photo&&beds&&active?{...photo,beds,active:active.id,guide:active.placement}:undefined}];
  });
  return withRoomViews(room,views,views.some(v=>v.id===room.activeViewId)?room.activeViewId!:views[0]?.id??'');
 });
 const remaining=updated.find(r=>r.id===roomId);
 let views=remaining?.views??(remaining?roomViews(remaining):[]);
 const viewId=destination.viewId??crypto.randomUUID();
 const prior=views.find(v=>v.id===viewId);
 const photo=destination.photoPreview??prior?.photoPreview??existing?.photoPreview;
 let prepared=photo;
 if(photo){
  const oldBeds=prior?.photoPreview?.beds??[];
  const id=Math.max(-1,...oldBeds.map(b=>b.id))+1;
  const template=destination.photoPreview?.beds[0]??photo.beds[0];
  const bed={...template,...bunk,id,locked:true};
  prepared={...photo,beds:[...oldBeds,bed],active:id,guide:bed.placement,step:4};
 }
 let wallNumber=views.length+1;while(views.some(v=>v.name===`Wall ${wallNumber}`))wallNumber++;
 const view={id:viewId,name:prior?.name??existing?.name??(views.length?`Wall ${wallNumber}`:'This wall'),bunks:[...(prior?.bunks??[]),bunk],bunkNumbers:[...(prior?.bunkNumbers??[]),number],photoPreview:prepared};
 views=prior?views.map(v=>v.id===viewId?view:v):[...views,view];
 target=withRoomViews(target,views,viewId);
 updated=updated.some(r=>r.id===roomId)?updated.map(r=>r.id===roomId?target!:r):[...updated,target];
 return {rooms:updated.filter(r=>r.bunks.length),target,number};
}
